/**
 * Payment Calculations Service
 *
 * Calculates metrics for different payment scenarios:
 * - Upfront payment (current default)
 * - 3-Year payment program (simple split, no interest)
 */

/**
 * Calculate 3-year payment metrics
 *
 * @param {number} treatmentCost - Total treatment cost
 * @param {number} annualSavings - Annual energy savings
 * @returns {Object} Payment metrics for 3-year program
 */
export function calculatePaymentMetrics(treatmentCost, annualSavings) {
  const monthlyPayment = treatmentCost / 36;
  const annualPayment = treatmentCost / 3;
  const monthlySavings = annualSavings / 12;

  return {
    // Payment schedule
    monthlyPayment,
    annualPayment,
    totalCost: treatmentCost, // Same as upfront (no interest)

    // Cash flow metrics
    monthlyCashFlow: monthlySavings - monthlyPayment,
    year1NetBenefit: annualSavings - annualPayment,
    year2NetBenefit: annualSavings - annualPayment,
    year3NetBenefit: annualSavings - annualPayment,
    year4NetBenefit: annualSavings, // Payments complete
    year5NetBenefit: annualSavings,

    // Status indicators
    isCashFlowPositiveMonthly: monthlySavings > monthlyPayment,
    isCashFlowPositiveYear1: annualSavings > annualPayment,

    // 5-year cumulative
    fiveYearCumulativeSavings: annualSavings * 5,
    fiveYearCumulativePayments: treatmentCost, // Paid off in Year 3
    fiveYearNetProfit: (annualSavings * 5) - treatmentCost,

    // Adjusted payback (same as upfront since no interest)
    adjustedPaybackMonths: (treatmentCost / annualSavings) * 12,

    // Payment schedule by year (for charts)
    paymentSchedule: [
      { year: 0, payment: 0, cumulative: 0 },
      { year: 1, payment: annualPayment, cumulative: annualPayment },
      { year: 2, payment: annualPayment, cumulative: annualPayment * 2 },
      { year: 3, payment: annualPayment, cumulative: treatmentCost },
      { year: 4, payment: 0, cumulative: treatmentCost },
      { year: 5, payment: 0, cumulative: treatmentCost },
    ],
  };
}

/**
 * Calculate upfront payment metrics for comparison
 *
 * @param {number} treatmentCost - Total treatment cost
 * @param {number} annualSavings - Annual energy savings
 * @returns {Object} Payment metrics for upfront payment
 */
export function calculateUpfrontMetrics(treatmentCost, annualSavings) {
  const monthlySavings = annualSavings / 12;
  const paybackMonths = (treatmentCost / annualSavings) * 12;

  return {
    totalCost: treatmentCost,
    year1Outlay: treatmentCost,
    year1NetBenefit: annualSavings - treatmentCost,
    monthlyCashFlow: monthlySavings, // After initial payment
    monthlyCashFlowYear1: (annualSavings - treatmentCost) / 12, // Including cost
    paybackMonths,
    fiveYearNetProfit: (annualSavings * 5) - treatmentCost,

    // Payment schedule by year (for charts)
    paymentSchedule: [
      { year: 0, payment: treatmentCost, cumulative: treatmentCost },
      { year: 1, payment: 0, cumulative: treatmentCost },
      { year: 2, payment: 0, cumulative: treatmentCost },
      { year: 3, payment: 0, cumulative: treatmentCost },
      { year: 4, payment: 0, cumulative: treatmentCost },
      { year: 5, payment: 0, cumulative: treatmentCost },
    ],
  };
}

/**
 * Format currency for display
 *
 * @param {number} value - Dollar amount
 * @returns {string} Formatted currency string
 */
export function formatPaymentCurrency(value) {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

// =============================================================================
// CFO-LEVEL FINANCIAL ANALYSIS
// =============================================================================

/**
 * Calculate Net Present Value of a payment stream
 *
 * NPV = sum of (payment / (1 + discountRate)^year)
 *
 * @param {number[]} payments - Array of annual payments (year 1, 2, 3...)
 * @param {number} discountRate - Company's cost of capital (e.g., 0.08 for 8%)
 * @returns {number} Net present value of payments
 */
export function calculateNPV(payments, discountRate) {
  return payments.reduce((npv, payment, index) => {
    const year = index + 1; // Year 1, 2, 3...
    return npv + payment / Math.pow(1 + discountRate, year);
  }, 0);
}

/**
 * Calculate capital preservation by year for 3-year payment
 *
 * Shows how much working capital stays in the business vs upfront payment
 *
 * @param {number} treatmentCost - Total treatment cost
 * @returns {Object} Capital retained by year
 */
export function calculateCapitalPreservation(treatmentCost) {
  const annualPayment = treatmentCost / 3;

  return {
    // How much capital is RETAINED in the business each year (vs paying upfront)
    year1Retained: treatmentCost - annualPayment, // 2/3 of cost still available
    year2Retained: treatmentCost - (annualPayment * 2), // 1/3 of cost still available
    year3Retained: 0, // Fully paid, no advantage

    // Average capital available across 3 years
    averageCapitalAvailable: (
      (treatmentCost - annualPayment) + // Year 1
      (treatmentCost - (annualPayment * 2)) + // Year 2
      0 // Year 3
    ) / 3,
  };
}

/**
 * Calculate comprehensive CFO financial analysis
 *
 * Includes NPV analysis, capital preservation, and accounting treatment comparison.
 *
 * @param {number} treatmentCost - Total treatment cost
 * @param {number} annualSavings - Annual energy savings
 * @param {number} discountRate - Company's cost of capital (default 8%)
 * @returns {Object} Complete CFO analysis
 */
export function calculateCFOAnalysis(treatmentCost, annualSavings, discountRate = 0.08) {
  const annualPayment = treatmentCost / 3;

  // NPV Analysis
  // Upfront: Full cost today = treatmentCost (no discounting needed)
  const upfrontNPV = treatmentCost;

  // 3-Year: Discount each annual payment
  const threeYearPayments = [annualPayment, annualPayment, annualPayment];
  const threeYearNPV = calculateNPV(threeYearPayments, discountRate);

  // NPV Advantage = what you "save" by deferring payment
  const npvAdvantage = upfrontNPV - threeYearNPV;
  const npvAdvantagePct = (npvAdvantage / treatmentCost) * 100;

  // Capital Preservation
  const capitalPreservation = calculateCapitalPreservation(treatmentCost);

  return {
    // NPV Analysis
    npv: {
      upfront: upfrontNPV,
      threeYear: threeYearNPV,
      advantage: npvAdvantage,
      advantagePct: npvAdvantagePct,
      discountRate: discountRate,
      discountRatePct: discountRate * 100,
    },

    // Capital Preservation
    capitalPreservation: {
      ...capitalPreservation,
      year1Advantage: capitalPreservation.year1Retained,
      year2Advantage: capitalPreservation.year2Retained,
    },

    // Accounting Treatment Comparison
    accounting: {
      upfront: {
        type: 'CapEx',
        treatment: 'Capitalized asset',
        depreciation: 'Depreciated over useful life',
        taxImpact: 'Deduction spread over depreciation period',
        budgetApproval: 'Requires CapEx approval process',
      },
      threeYear: {
        type: 'OpEx',
        treatment: 'Operating expense',
        depreciation: 'N/A - expensed when paid',
        taxImpact: 'Full deduction in year incurred',
        budgetApproval: 'Simpler OpEx budget allocation',
      },
    },

    // Summary Messages
    summary: {
      npvMessage: `Save ${formatPaymentCurrency(npvAdvantage)} (${npvAdvantagePct.toFixed(1)}%) through time value of money at ${(discountRate * 100).toFixed(0)}% discount rate`,
      capitalMessage: `Keep ${formatPaymentCurrency(capitalPreservation.year1Retained)} in working capital during Year 1`,
      accountingMessage: '3-Year payments may qualify as OpEx for simpler budgeting and immediate tax deductions',
    },
  };
}
